import {
  Alert,
  AlertIcon,
  Avatar,
  Badge,
  Box,
  Button,
  Flex,
  HStack,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Spinner,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  VStack,
} from '@elkaso-app/web-design';
import { useGetSourceBrandLocationChildrenApi } from 'apis/crm-source-brand-locations/use-get-source-brand-location-children-api';
import React from 'react';
import { FiGlobe, FiMapPin, FiPhone, FiStar } from 'react-icons/fi';

interface LocationChildrenModalProps {
  isOpen: boolean;
  onClose: () => void;
  locationId: number;
}

const LocationChildrenModal: React.FC<LocationChildrenModalProps> = ({ isOpen, onClose, locationId }) => {
  const { data: childrenData, isLoading, error } = useGetSourceBrandLocationChildrenApi(locationId);

  const renderLocationInfo = (child: any) => (
    <VStack align='start' spacing={1}>
      <HStack spacing={2}>
        <FiMapPin color='#3182CE' size={14} />
        <Text fontWeight='semibold' fontSize='sm'>
          {child.name}
        </Text>
      </HStack>
      {child.address && (
        <Text fontSize='xs' color='gray.600' maxW='250px' noOfLines={2}>
          {child.address}
        </Text>
      )}
      <HStack spacing={2}>
        <Badge colorScheme='blue' variant='subtle' size='sm'>
          {child.sourcePlatform}
        </Badge>
        <Text fontSize='xs' color='gray.500'>
          ID: {child.sourceExternalId}
        </Text>
      </HStack>
    </VStack>
  );

  const renderBrandInfo = (child: any) => (
    <HStack spacing={2}>
      {child.brand.image && <Avatar size='sm' src={child.brand.image} name={child.brand.name} />}
      <VStack align='start' spacing={0}>
        <Text fontWeight='medium' fontSize='sm'>
          {child.brand.name}
        </Text>
        <Badge colorScheme='gray' variant='subtle' size='sm'>
          {child.brand.countryCode}
        </Badge>
      </VStack>
    </HStack>
  );

  const renderStats = (child: any) => (
    <VStack align='start' spacing={1}>
      {child.rating && typeof child.rating === 'number' && (
        <HStack spacing={1}>
          <FiStar color='#ECC94B' size={14} />
          <Text fontSize='sm' fontWeight='medium'>
            {child.rating.toFixed(1)}
          </Text>
          {child.ratingsTotal && (
            <Text fontSize='xs' color='gray.500'>
              ({child.ratingsTotal})
            </Text>
          )}
        </HStack>
      )}
      {child.reviewsTotal && (
        <Text fontSize='xs' color='gray.600'>
          💬 {child.reviewsTotal} reviews
        </Text>
      )}
      {child.businessStatus && (
        <Badge colorScheme={child.businessStatus === 'OPERATIONAL' ? 'green' : 'red'} variant='subtle' size='sm'>
          {child.businessStatus}
        </Badge>
      )}
    </VStack>
  );

  const renderContact = (child: any) => (
    <VStack align='start' spacing={1}>
      {child.phoneNumber && (
        <HStack spacing={1}>
          <FiPhone size={12} />
          <Text fontSize='xs'>{child.phoneNumber}</Text>
        </HStack>
      )}
      {child.website && (
        <HStack spacing={1}>
          <FiGlobe size={12} />
          <Text fontSize='xs' color='blue.600' isTruncated maxW='150px'>
            {child.website}
          </Text>
        </HStack>
      )}
      {child.geoLocation && (
        <Text fontSize='xs' color='gray.600'>
          📍 {child.geoLocation.name}
        </Text>
      )}
    </VStack>
  );

  return (
    <Modal isOpen={isOpen} onClose={onClose} size='6xl'>
      <ModalOverlay />
      <ModalContent maxW='1200px'>
        <ModalHeader>Location Children</ModalHeader>
        <ModalCloseButton />

        <ModalBody>
          <VStack spacing={4} align='stretch'>
            <Alert status='info'>
              <AlertIcon />
              These are the child locations that belong to this parent location.
            </Alert>

            {isLoading ? (
              <Flex justify='center' py={8}>
                <VStack spacing={4}>
                  <Spinner size='lg' />
                  <Text>Loading children locations...</Text>
                </VStack>
              </Flex>
            ) : error ? (
              <Alert status='error'>
                <AlertIcon />
                Failed to load children locations.
              </Alert>
            ) : childrenData?.children?.length ? (
              <Box>
                <Text fontWeight='bold' color='gray.700' mb={4}>
                  Found {childrenData.total} child location{childrenData.total !== 1 ? 's' : ''}
                </Text>

                <Table variant='simple' size='sm'>
                  <Thead>
                    <Tr>
                      <Th>Location</Th>
                      <Th>Brand</Th>
                      <Th>Stats</Th>
                      <Th>Contact</Th>
                      <Th>Created</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {childrenData.children.map((child) => (
                      <Tr key={child.id}>
                        <Td>{renderLocationInfo(child)}</Td>
                        <Td>{renderBrandInfo(child)}</Td>
                        <Td>{renderStats(child)}</Td>
                        <Td>{renderContact(child)}</Td>
                        <Td>
                          <Text fontSize='xs' color='gray.500'>
                            {new Date(child.createdAt).toLocaleDateString()}
                          </Text>
                        </Td>
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
              </Box>
            ) : (
              <Alert status='info'>
                <AlertIcon />
                This location has no child locations.
              </Alert>
            )}

            {/* Cost Information */}
            {childrenData?.children?.some((child) => child.avgCostPerCouple) && (
              <Box p={4} border='1px' borderColor='gray.200' borderRadius='md' bg='gray.50'>
                <Text fontWeight='bold' color='gray.700' mb={2}>
                  Cost Information
                </Text>
                <VStack align='start' spacing={2}>
                  {childrenData.children
                    .filter((child) => child.avgCostPerCouple)
                    .map((child) => (
                      <HStack key={child.id} spacing={2}>
                        <Text fontSize='sm' fontWeight='medium'>
                          {child.name}:
                        </Text>
                        <Text fontSize='sm'>
                          {child.avgCostPerCouple} {child.avgCostCurrency || 'AED'} per couple
                        </Text>
                        {child.priceLevel && (
                          <Badge colorScheme='green' variant='subtle' size='sm'>
                            {'$'.repeat(child.priceLevel)}
                          </Badge>
                        )}
                      </HStack>
                    ))}
                </VStack>
              </Box>
            )}
          </VStack>
        </ModalBody>

        <ModalFooter>
          <Button onClick={onClose}>Close</Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default LocationChildrenModal;
